import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  ThemeProvider,
  createTheme,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

// Zoho Books-inspired theme
const theme = createTheme({
  typography: {
    fontFamily: '"Poppins", "Lato", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 13,
  },
  palette: {
    primary: {
      main: "#2D7FF9",
      light: "#5B9EFA",
      dark: "#1E5FD9",
    },
    success: {
      main: "#0BBE87",
    },
    error: {
      main: "#E85858",
    },
    background: {
      default: "#F9FAFB",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#212529",
      secondary: "#6C757D",
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#FFFFFF",
            "& fieldset": {
              borderColor: "#D1D5DB",
            },
            "&:hover fieldset": {
              borderColor: "#9CA3AF",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#2D7FF9",
              borderWidth: "1px",
            },
          },
          "& .MuiInputBase-input": {
            fontSize: "13px",
            padding: "10px 12px",
          },
          "& .MuiInputLabel-root": {
            fontSize: "13px",
            color: "#6C757D",
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "13px",
          fontWeight: 500,
          padding: "8px 16px",
          borderRadius: "4px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
        contained: {
          "&:hover": {
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "13px",
          padding: "12px 16px",
          borderBottom: "1px solid #E5E7EB",
        },
        head: {
          fontWeight: 600,
          color: "#374151",
          backgroundColor: "#F9FAFB",
          borderBottom: "2px solid #E5E7EB",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          borderRadius: "6px",
        },
      },
    },
  },
});

// Initial text status options
const initialTextStatuses = [
  { text: "Eligible", symbol: "✓", color: { bg: "#D1FAE5", color: "#065F46" } },
  { text: "TBA", symbol: "TBA", color: { bg: "#FEF3C7", color: "#92400E" } },
  {
    text: "Chargeable Services",
    symbol: "*",
    color: { bg: "#DBEAFE", color: "#1E40AF" },
  },
  {
    text: "Not Applicable",
    symbol: "✗",
    color: { bg: "#F3F4F6", color: "#374151" },
  },
];

// Mock data for existing memberships
const initialMemberships = [
  {
    id: 1,
    name: "Gold Membership",
    features: [
      { name: "Virtual Internship", type: "text", statusValue: "Eligible" },
      { name: "Awards and Recognition", type: "text", statusValue: "Eligible" },
      { name: "Technical Workshop", type: "text", statusValue: "TBA" },
      {
        name: "Certification Programs",
        type: "text",
        statusValue: "Chargeable Services",
      },
      { name: "Guest Lectures", type: "text", statusValue: "Eligible" },
      { name: "Max Students", type: "count", statusValue: "100" },
    ],
  },
  {
    id: 2,
    name: "Silver Membership",
    features: [
      { name: "Virtual Internship", type: "text", statusValue: "TBA" },
      { name: "Technical Workshop", type: "text", statusValue: "Eligible" },
      { name: "Guest Lectures", type: "text", statusValue: "Not Applicable" },
      { name: "Max Students", type: "count", statusValue: "50" },
    ],
  },
];

const getStatusColor = (statusValue, type, textStatuses) => {
  if (type === "count") {
    return { bg: "#E0E7FF", color: "#3730A3" };
  }
  const statusType = textStatuses.find((st) => st.text === statusValue);
  return statusType?.color || { bg: "#E0E7FF", color: "#3730A3" };
};

const getStatusDisplay = (statusValue, type, textStatuses) => {
  if (type === "count") {
    return statusValue;
  }
  const statusType = textStatuses.find((st) => st.text === statusValue);
  if (statusType) {
    // If symbol and text are the same (like TBA), show only once
    if (statusType.symbol === statusType.text) {
      return statusType.text;
    }
    // Otherwise show symbol + text
    return `${statusType.symbol} ${statusType.text}`;
  }
  return statusValue;
};

const MembershipFormSystem = () => {
  const [existingMemberships, setExistingMemberships] =
    useState(initialMemberships);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [features, setFeatures] = useState([]);
  const [openAddFeatureDialog, setOpenAddFeatureDialog] = useState(false);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [openCreateMembershipDialog, setOpenCreateMembershipDialog] =
    useState(false);
  const [newMembershipName, setNewMembershipName] = useState("");
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    index: null,
  });

  // Text statuses management
  const [textStatuses, setTextStatuses] = useState(initialTextStatuses);
  const [openCreateTextStatusDialog, setOpenCreateTextStatusDialog] =
    useState(false);
  const [newStatusText, setNewStatusText] = useState("");
  const [newStatusSymbol, setNewStatusSymbol] = useState("");
  const [currentEditingFeature, setCurrentEditingFeature] = useState(null);

  // States for inline editing
  const [editingStatus, setEditingStatus] = useState(null);
  const [tempStatusType, setTempStatusType] = useState("");
  const [tempStatusValue, setTempStatusValue] = useState("");
  const [featureSearchQuery, setFeatureSearchQuery] = useState("");

  // States for Add Feature Dialog
  const [featureTypes, setFeatureTypes] = useState({});
  const [featureStatusValues, setFeatureStatusValues] = useState({});

  // Available features list for selection
  const availableFeatures = [
    "Virtual Internship",
    "Awards and Recognition",
    "Technical Workshop",
    "Certification Programs",
    "Guest Lectures",
    "Industry Visits",
    "Hackathons",
    "Mentorship Program",
    "Career Counseling",
    "Skill Development",
    "Research Projects",
    "Networking Events",
    "Job Placement Assistance",
    "Resume Building",
    "Interview Preparation",
    "Max Students",
    "Max Duration (Days)",
  ];

  // Handle membership selection
  const handleMembershipChange = (event, newValue) => {
    if (newValue?.isCreateNew) {
      setOpenCreateMembershipDialog(true);
      setInputValue("");
      return;
    }

    if (typeof newValue === "string") {
      setSelectedMembership({
        id: null,
        name: newValue,
        features: [],
        isNew: true,
      });
      setFeatures([]);
    } else if (newValue && newValue.inputValue) {
      setSelectedMembership({
        id: null,
        name: newValue.inputValue,
        features: [],
        isNew: true,
      });
      setFeatures([]);
    } else {
      setSelectedMembership(newValue);
      setFeatures(newValue ? [...newValue.features] : []);
    }
  };

  // Handle feature type change in dialog
  const handleFeatureTypeChange = (featureName, type) => {
    setFeatureTypes((prev) => ({
      ...prev,
      [featureName]: type,
    }));

    // Set default value based on type
    if (type === "text") {
      setFeatureStatusValues((prev) => ({
        ...prev,
        [featureName]: "TBA",
      }));
    } else if (type === "count") {
      setFeatureStatusValues((prev) => ({
        ...prev,
        [featureName]: "0",
      }));
    }
  };

  // Handle feature status value change in dialog
  const handleFeatureStatusValueChange = (featureName, value) => {
    setFeatureStatusValues((prev) => ({
      ...prev,
      [featureName]: value,
    }));
  };

  // Handle add new features
  const handleAddFeature = () => {
    if (selectedFeatures.length > 0) {
      const newFeaturesToAdd = selectedFeatures.map((featureName) => {
        const type = featureTypes[featureName] || "text";
        const statusValue =
          featureStatusValues[featureName] || (type === "text" ? "TBA" : "0");

        return {
          name: featureName,
          type: type,
          statusValue: statusValue,
        };
      });
      setFeatures([...features, ...newFeaturesToAdd]);
      setSelectedFeatures([]);
      setFeatureTypes({});
      setFeatureStatusValues({});
      setOpenAddFeatureDialog(false);
    }
  };

  // Handle checkbox toggle
  const handleFeatureToggle = (featureName) => {
    setSelectedFeatures((prev) => {
      const newSelection = prev.includes(featureName)
        ? prev.filter((name) => name !== featureName)
        : [...prev, featureName];

      if (!newSelection.includes(featureName)) {
        setFeatureTypes((prevTypes) => {
          const newTypes = { ...prevTypes };
          delete newTypes[featureName];
          return newTypes;
        });
        setFeatureStatusValues((prevValues) => {
          const newValues = { ...prevValues };
          delete newValues[featureName];
          return newValues;
        });
      } else if (!featureTypes[featureName]) {
        setFeatureTypes((prevTypes) => ({
          ...prevTypes,
          [featureName]: "text",
        }));
        setFeatureStatusValues((prevValues) => ({
          ...prevValues,
          [featureName]: "TBA",
        }));
      }

      return newSelection;
    });
  };

  // Handle status edit inline
  const handleStatusEdit = (index, currentType, currentValue) => {
    setEditingStatus(index);
    setTempStatusType(currentType);
    setTempStatusValue(currentValue);
  };

  const handleStatusSave = (index) => {
    if (tempStatusType === "count" && isNaN(tempStatusValue)) {
      alert("Please enter a valid number for count");
      return;
    }

    if (!tempStatusValue.trim()) {
      setEditingStatus(null);
      return;
    }

    const updatedFeatures = [...features];
    updatedFeatures[index].type = tempStatusType;
    updatedFeatures[index].statusValue = tempStatusValue;
    setFeatures(updatedFeatures);

    setEditingStatus(null);
    setTempStatusType("");
    setTempStatusValue("");
  };

  const handleStatusCancel = () => {
    setEditingStatus(null);
    setTempStatusType("");
    setTempStatusValue("");
  };

  const handleTypeChange = (newType) => {
    setTempStatusType(newType);

    // Set default value based on type
    if (newType === "text") {
      setTempStatusValue("TBA");
    } else if (newType === "count") {
      setTempStatusValue("0");
    }
  };

  // Handle create new text status
  const handleCreateTextStatus = () => {
    if (newStatusText.trim() && newStatusSymbol.trim()) {
      const newStatus = {
        text: newStatusText.trim(),
        symbol: newStatusSymbol.trim(),
        color: { bg: "#E0E7FF", color: "#3730A3" }, // Default colors
      };
      setTextStatuses([...textStatuses, newStatus]);

      // Update the feature status value with the newly created status
      if (currentEditingFeature) {
        handleFeatureStatusValueChange(
          currentEditingFeature,
          newStatusText.trim()
        );
      }

      setNewStatusText("");
      setNewStatusSymbol("");
      setCurrentEditingFeature(null);
      setOpenCreateTextStatusDialog(false);
    }
  };

  // Handle create new membership from dialog
  const handleCreateNewMembership = () => {
    if (newMembershipName.trim()) {
      const newMembership = {
        id: Date.now(),
        name: newMembershipName.trim(),
        features: [],
        isNew: true,
      };
      setSelectedMembership(newMembership);
      setFeatures([]);
      setInputValue(newMembershipName.trim());
      setNewMembershipName("");
      setOpenCreateMembershipDialog(false);
    }
  };

  // Handle delete feature with confirmation
  const handleDeleteFeatureClick = (index) => {
    setDeleteConfirmDialog({ open: true, index });
  };

  const handleDeleteFeatureConfirm = () => {
    const index = deleteConfirmDialog.index;
    const updatedFeatures = features.filter((_, i) => i !== index);
    setFeatures(updatedFeatures);
    setDeleteConfirmDialog({ open: false, index: null });
  };

  const handleDeleteFeatureCancel = () => {
    setDeleteConfirmDialog({ open: false, index: null });
  };

  // Handle form submit
  const handleSubmit = () => {
    const formData = {
      membership: selectedMembership,
      isNewMembership: selectedMembership?.isNew || false,
      features: features,
      totalFeatures: features.length,
      typeBreakdown: features.reduce((acc, feature) => {
        acc[feature.type] = (acc[feature.type] || 0) + 1;
        return acc;
      }, {}),
    };
    console.log("Form Data:", formData);

    if (selectedMembership?.isNew) {
      const newMembershipToAdd = {
        id: selectedMembership.id,
        name: selectedMembership.name,
        features: [...features],
        isNew: false,
      };
      setExistingMemberships([...existingMemberships, newMembershipToAdd]);
    } else {
      const updatedMemberships = existingMemberships.map((m) =>
        m.id === selectedMembership.id ? { ...m, features: [...features] } : m
      );
      setExistingMemberships(updatedMemberships);
    }

    alert("Membership saved successfully! Check console for details.");

    setSelectedMembership(null);
    setFeatures([]);
    setInputValue("");
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#F9FAFB",
          py: 4,
          px: 3,
          pb: selectedMembership ? "100px" : 4,
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: "#212529",
                mb: 0.5,
              }}
            >
              Create/Edit Membership
            </Typography>
            <Typography variant="body2" sx={{ color: "#6C757D" }}>
              Manage membership details with text or count status types
            </Typography>
          </Box>

          {/* Main Form */}
          <Paper sx={{ p: 3, mb: 3 }}>
            {/* Membership Selection Section */}
            <Box
              sx={{
                mb: 4,
                transition: "all 0.3s ease-in-out",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#374151",
                  mb: 1.5,
                  fontSize: "13px",
                }}
              >
                Membership Details
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Autocomplete
                  value={selectedMembership}
                  onChange={handleMembershipChange}
                  inputValue={inputValue}
                  onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                  }}
                  options={[
                    {
                      id: "create-new",
                      name: "Create New...",
                      isCreateNew: true,
                    },
                    ...existingMemberships,
                  ]}
                  getOptionLabel={(option) => {
                    if (typeof option === "string") {
                      return option;
                    }
                    if (option.inputValue) {
                      return option.inputValue;
                    }
                    return option.name || "";
                  }}
                  filterOptions={(options, params) => {
                    const filtered = options.filter((option) => {
                      if (option.isCreateNew) return true;
                      return option.name
                        ?.toLowerCase()
                        .includes(params.inputValue.toLowerCase());
                    });
                    return filtered;
                  }}
                  selectOnFocus
                  clearOnBlur
                  handleHomeEndKeys
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option.isCreateNew ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: "100%",
                            py: 0.5,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              transform: "translateX(4px)",
                            },
                          }}
                        >
                          <AddIcon sx={{ fontSize: 18, color: "#2D7FF9" }} />
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#2D7FF9",
                              fontWeight: 600,
                            }}
                          >
                            {option.name}
                          </Typography>
                        </Box>
                      ) : option.isNew ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <AddIcon sx={{ fontSize: 18, color: "#2D7FF9" }} />
                          <Typography
                            sx={{ fontSize: "13px", color: "#2D7FF9" }}
                          >
                            {option.name}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: "13px" }}>
                          {option.name}
                        </Typography>
                      )}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select membership"
                      size="small"
                    />
                  )}
                  sx={{ flex: 1, maxWidth: 500 }}
                />

                {selectedMembership?.isNew && (
                  <Chip
                    label="New Membership"
                    size="small"
                    sx={{
                      backgroundColor: "#D1FAE5",
                      color: "#065F46",
                      fontWeight: 600,
                      fontSize: "11px",
                      animation: "fadeIn 0.3s ease-in-out",
                      "@keyframes fadeIn": {
                        from: { opacity: 0, transform: "scale(0.9)" },
                        to: { opacity: 1, transform: "scale(1)" },
                      },
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Features Table Section */}
            {selectedMembership && (
              <Box
                sx={{
                  animation: "slideIn 0.4s ease-out",
                  "@keyframes slideIn": {
                    from: {
                      opacity: 0,
                      transform: "translateY(20px)",
                    },
                    to: {
                      opacity: 1,
                      transform: "translateY(0)",
                    },
                  },
                }}
              >
                <Divider sx={{ mb: 3 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#374151",
                        fontSize: "13px",
                      }}
                    >
                      Features ({features.length})
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#9CA3AF",
                        fontSize: "11px",
                        fontStyle: "italic",
                      }}
                    >
                      Define type (Text/Count) and status for each feature
                    </Typography>
                  </Box>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => setOpenAddFeatureDialog(true)}
                    size="small"
                    variant="outlined"
                    sx={{
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 8px rgba(45, 127, 249, 0.2)",
                      },
                    }}
                  >
                    Add New Feature
                  </Button>
                </Box>

                {features.length === 0 ? (
                  <Box
                    sx={{
                      p: 6,
                      textAlign: "center",
                      border: "2px dashed #E5E7EB",
                      borderRadius: "6px",
                      backgroundColor: "#FAFBFC",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "#6C757D", mb: 2, fontSize: "13px" }}
                    >
                      No features added yet
                    </Typography>
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => setOpenAddFeatureDialog(true)}
                      variant="contained"
                      size="small"
                      sx={{
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 12px rgba(45, 127, 249, 0.3)",
                        },
                      }}
                    >
                      Add Your First Feature
                    </Button>
                  </Box>
                ) : (
                  <TableContainer
                    component={Paper}
                    variant="outlined"
                    sx={{ border: "1px solid #E5E7EB" }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: "10%" }}>#</TableCell>
                          <TableCell sx={{ width: "45%" }}>
                            Feature Name
                          </TableCell>
                          <TableCell sx={{ width: "35%" }}>Status</TableCell>
                          <TableCell sx={{ width: "10%", textAlign: "center" }}>
                            Action
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {features.map((feature, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              "&:hover": {
                                backgroundColor: "#F9FAFB",
                              },
                              animation: "fadeInRow 0.3s ease-out",
                              animationDelay: `${index * 0.05}s`,
                              animationFillMode: "backwards",
                              "@keyframes fadeInRow": {
                                from: {
                                  opacity: 0,
                                  transform: "translateX(-10px)",
                                },
                                to: {
                                  opacity: 1,
                                  transform: "translateX(0)",
                                },
                              },
                            }}
                          >
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "13px",
                                  color: "#6C757D",
                                  fontWeight: 600,
                                }}
                              >
                                {index + 1}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "13px",
                                  color: "#212529",
                                  fontWeight: 500,
                                }}
                              >
                                {feature.name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {editingStatus === index ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                  }}
                                >
                                  <FormControl
                                    size="small"
                                    sx={{ minWidth: 100 }}
                                  >
                                    <Select
                                      value={tempStatusType}
                                      onChange={(e) =>
                                        handleTypeChange(e.target.value)
                                      }
                                      sx={{ fontSize: "13px" }}
                                    >
                                      <MenuItem
                                        value="text"
                                        sx={{ fontSize: "13px" }}
                                      >
                                        Text
                                      </MenuItem>
                                      <MenuItem
                                        value="count"
                                        sx={{ fontSize: "13px" }}
                                      >
                                        Count
                                      </MenuItem>
                                    </Select>
                                  </FormControl>

                                  {tempStatusType === "count" ? (
                                    <TextField
                                      size="small"
                                      type="number"
                                      value={tempStatusValue}
                                      onChange={(e) =>
                                        setTempStatusValue(e.target.value)
                                      }
                                      placeholder="Enter count"
                                      sx={{
                                        width: 120,
                                        "& .MuiOutlinedInput-root": {
                                          fontSize: "13px",
                                        },
                                      }}
                                    />
                                  ) : (
                                    <Autocomplete
                                      freeSolo
                                      options={[
                                        ...textStatuses.map((st) => st.text),
                                        "Create New...",
                                      ]}
                                      value={tempStatusValue}
                                      onChange={(event, newValue) => {
                                        if (newValue === "Create New...") {
                                          setOpenCreateTextStatusDialog(true);
                                        } else if (newValue) {
                                          setTempStatusValue(newValue);
                                        }
                                      }}
                                      onInputChange={(event, newInputValue) => {
                                        if (newInputValue !== "Create New...") {
                                          setTempStatusValue(newInputValue);
                                        }
                                      }}
                                      renderOption={(props, option) => (
                                        <li {...props}>
                                          {option === "Create New..." ? (
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                              }}
                                            >
                                              <AddIcon
                                                sx={{
                                                  fontSize: 16,
                                                  color: "#2D7FF9",
                                                }}
                                              />
                                              <Typography
                                                sx={{
                                                  fontSize: "13px",
                                                  color: "#2D7FF9",
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {option}
                                              </Typography>
                                            </Box>
                                          ) : (
                                            <Typography
                                              sx={{ fontSize: "13px" }}
                                            >
                                              {option}
                                            </Typography>
                                          )}
                                        </li>
                                      )}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          size="small"
                                          placeholder="Select or type"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              fontSize: "13px",
                                            },
                                          }}
                                        />
                                      )}
                                      sx={{ flex: 1, minWidth: 150 }}
                                    />
                                  )}

                                  <IconButton
                                    size="small"
                                    onClick={() => handleStatusSave(index)}
                                    color="success"
                                    sx={{
                                      transition: "all 0.2s ease",
                                      "&:hover": {
                                        transform: "scale(1.1)",
                                      },
                                    }}
                                  >
                                    <CheckIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={handleStatusCancel}
                                    color="error"
                                    sx={{
                                      transition: "all 0.2s ease",
                                      "&:hover": {
                                        transform: "scale(1.1)",
                                      },
                                    }}
                                  >
                                    <CloseIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    px: 2,
                                    py: 1,
                                    borderRadius: "4px",
                                    backgroundColor: getStatusColor(
                                      feature.statusValue,
                                      feature.type,
                                      textStatuses
                                    ).bg,
                                    color: getStatusColor(
                                      feature.statusValue,
                                      feature.type,
                                      textStatuses
                                    ).color,
                                    fontWeight: 500,
                                    fontSize: "13px",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                      opacity: 0.8,
                                      transform: "translateY(-1px)",
                                    },
                                  }}
                                  onClick={() =>
                                    handleStatusEdit(
                                      index,
                                      feature.type,
                                      feature.statusValue
                                    )
                                  }
                                >
                                  {getStatusDisplay(
                                    feature.statusValue,
                                    feature.type,
                                    textStatuses
                                  )}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              <Button
                                size="small"
                                onClick={() => handleDeleteFeatureClick(index)}
                                color="error"
                                variant="text"
                                startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                                sx={{
                                  fontSize: "12px",
                                  padding: "4px 8px",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    transform: "scale(1.05)",
                                  },
                                }}
                              >
                                Remove
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Submit Button - Fixed Footer */}
      {selectedMembership && (
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "#FFFFFF",
            borderTop: "1px solid #E5E7EB",
            boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.05)",
            zIndex: 1000,
            animation: "slideUp 0.3s ease-out",
            "@keyframes slideUp": {
              from: {
                opacity: 0,
                transform: "translateY(100%)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          <Box
            sx={{
              mx: "auto",
              px: 3,
              py: 2,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setSelectedMembership(null);
                setFeatures([]);
                setInputValue("");
              }}
              sx={{
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSubmit}
              disabled={!selectedMembership || features.length === 0}
              sx={{
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 12px rgba(45, 127, 249, 0.3)",
                },
              }}
            >
              Save Membership
            </Button>
          </Box>
        </Box>
      )}

      {/* Add Feature Dialog */}
      <Dialog
        open={openAddFeatureDialog}
        onClose={() => {
          setOpenAddFeatureDialog(false);
          setSelectedFeatures([]);
          setFeatureTypes({});
          setFeatureStatusValues({});
          setFeatureSearchQuery("");
        }}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          timeout: 400,
        }}
        PaperProps={{
          sx: {
            animation: openAddFeatureDialog ? "slideUp 0.3s ease-out" : "none",
            "@keyframes slideUp": {
              from: {
                opacity: 0,
                transform: "translateY(20px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "15px",
            fontWeight: 600,
            pb: 2,
          }}
        >
          Select Features & Define Type
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2 }}>
          {/* Search Bar */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search features..."
            value={featureSearchQuery}
            onChange={(e) => setFeatureSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ fontSize: 18, color: "#9CA3AF", mr: 1 }} />
              ),
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
              },
            }}
          />

          {/* Selected Count */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
              px: 0.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "12px",
                color: "#6C757D",
                fontWeight: 500,
              }}
            >
              {selectedFeatures.length > 0
                ? `${selectedFeatures.length} selected`
                : "Select features to add"}
            </Typography>
            {selectedFeatures.length > 0 && (
              <Button
                size="small"
                onClick={() => {
                  setSelectedFeatures([]);
                  setFeatureTypes({});
                  setFeatureStatusValues({});
                }}
                sx={{
                  fontSize: "11px",
                  textTransform: "none",
                  color: "#E85858",
                  p: 0,
                  minWidth: "auto",
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Clear All
              </Button>
            )}
          </Box>

          {/* Features List */}
          <Box
            sx={{
              maxHeight: "400px",
              overflowY: "auto",
              border: "1px solid #E5E7EB",
              borderRadius: "6px",
              backgroundColor: "#FAFBFC",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#F3F4F6",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#D1D5DB",
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: "#9CA3AF",
                },
              },
            }}
          >
            <Box sx={{ p: 1 }}>
              {availableFeatures
                .filter(
                  (feature) =>
                    !features.some((f) => f.name === feature) &&
                    feature
                      .toLowerCase()
                      .includes(featureSearchQuery.toLowerCase())
                )
                .map((feature) => (
                  <Box
                    key={feature}
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: "6px",
                      border: "1px solid #E5E7EB",
                      backgroundColor: "#FFFFFF",
                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                      transition: "background-color 0.2s ease",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedFeatures.includes(feature)}
                          onChange={() => handleFeatureToggle(feature)}
                          size="small"
                          sx={{
                            color: "#9CA3AF",
                            p: 0.5,
                            "&.Mui-checked": {
                              color: "#2D7FF9",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#212529",
                            fontWeight: 500,
                          }}
                        >
                          {feature}
                        </Typography>
                      }
                      sx={{ mx: 0, mb: 1 }}
                    />

                    {selectedFeatures.includes(feature) && (
                      <Box sx={{ ml: 4, mt: 1 }}>
                        {/* Radio Button Group for Type Selection */}
                        <Box sx={{ mb: 1 }}>
                          <RadioGroup
                            row
                            value={featureTypes[feature] || "text"}
                            onChange={(e) =>
                              handleFeatureTypeChange(feature, e.target.value)
                            }
                            sx={{
                              gap: 2,
                            }}
                          >
                            <FormControlLabel
                              value="text"
                              control={
                                <Radio
                                  size="small"
                                  sx={{
                                    p: 0.5,
                                    "& .MuiSvgIcon-root": {
                                      fontSize: 18,
                                    },
                                  }}
                                />
                              }
                              label={
                                <Typography sx={{ fontSize: "12px", ml: 0.5 }}>
                                  Text
                                </Typography>
                              }
                              sx={{ mr: 1 }}
                            />
                            <FormControlLabel
                              value="count"
                              control={
                                <Radio
                                  size="small"
                                  sx={{
                                    p: 0.5,
                                    "& .MuiSvgIcon-root": {
                                      fontSize: 18,
                                    },
                                  }}
                                />
                              }
                              label={
                                <Typography sx={{ fontSize: "12px", ml: 0.5 }}>
                                  Count
                                </Typography>
                              }
                              sx={{ mr: 0 }}
                            />
                          </RadioGroup>
                        </Box>

                        {/* Value Input based on type */}
                        {featureTypes[feature] === "count" ? (
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Count Value"
                            value={featureStatusValues[feature] || "0"}
                            onChange={(e) =>
                              handleFeatureStatusValueChange(
                                feature,
                                e.target.value
                              )
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                fontSize: "13px",
                              },
                            }}
                          />
                        ) : (
                          <Autocomplete
                            freeSolo
                            fullWidth
                            options={[
                              ...textStatuses.map((st) => st.text),
                              "Create New...",
                            ]}
                            value={featureStatusValues[feature] || "TBA"}
                            onChange={(event, newValue) => {
                              if (newValue === "Create New...") {
                                setCurrentEditingFeature(feature);
                                setOpenCreateTextStatusDialog(true);
                              } else if (newValue) {
                                handleFeatureStatusValueChange(
                                  feature,
                                  newValue
                                );
                              }
                            }}
                            onInputChange={(event, newInputValue) => {
                              if (newInputValue !== "Create New...") {
                                handleFeatureStatusValueChange(
                                  feature,
                                  newInputValue
                                );
                              }
                            }}
                            renderOption={(props, option) => (
                              <li {...props}>
                                {option === "Create New..." ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <AddIcon
                                      sx={{ fontSize: 16, color: "#2D7FF9" }}
                                    />
                                    <Typography
                                      sx={{
                                        fontSize: "13px",
                                        color: "#2D7FF9",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {option}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: "3px",
                                        backgroundColor:
                                          textStatuses.find(
                                            (st) => st.text === option
                                          )?.color.bg || "#E0E7FF",
                                        color:
                                          textStatuses.find(
                                            (st) => st.text === option
                                          )?.color.color || "#3730A3",
                                        fontSize: "11px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {textStatuses.find(
                                        (st) => st.text === option
                                      )?.symbol || ""}
                                    </Box>
                                    <Typography sx={{ fontSize: "13px" }}>
                                      {option}
                                    </Typography>
                                  </Box>
                                )}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Status"
                                size="small"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    fontSize: "13px",
                                  },
                                }}
                              />
                            )}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                ))}
            </Box>
            {availableFeatures.filter(
              (feature) =>
                !features.some((f) => f.name === feature) &&
                feature.toLowerCase().includes(featureSearchQuery.toLowerCase())
            ).length === 0 && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: "13px",
                  color: "#9CA3AF",
                  textAlign: "center",
                  py: 4,
                }}
              >
                {featureSearchQuery
                  ? "No features found"
                  : "All features have been added"}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={() => {
              setOpenAddFeatureDialog(false);
              setSelectedFeatures([]);
              setFeatureTypes({});
              setFeatureStatusValues({});
              setFeatureSearchQuery("");
            }}
            size="small"
            sx={{ fontSize: "13px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddFeature}
            variant="contained"
            disabled={selectedFeatures.length === 0}
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            size="small"
            sx={{ fontSize: "13px" }}
          >
            Add {selectedFeatures.length > 0 && `(${selectedFeatures.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Text Status Dialog */}
      <Dialog
        open={openCreateTextStatusDialog}
        onClose={() => {
          setOpenCreateTextStatusDialog(false);
          setNewStatusText("");
          setNewStatusSymbol("");
          setCurrentEditingFeature(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 600 }}>
          Create New Text Status
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Status Text"
            value={newStatusText}
            onChange={(e) => setNewStatusText(e.target.value)}
            placeholder="e.g., Eligible, Premium"
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Symbol"
            value={newStatusSymbol}
            onChange={(e) => setNewStatusSymbol(e.target.value)}
            placeholder="e.g., ✓, *, #"
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setOpenCreateTextStatusDialog(false);
              setNewStatusText("");
              setNewStatusSymbol("");
              setCurrentEditingFeature(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateTextStatus}
            variant="contained"
            disabled={!newStatusText.trim() || !newStatusSymbol.trim()}
            startIcon={<AddIcon />}
          >
            Create Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Membership Dialog */}
      <Dialog
        open={openCreateMembershipDialog}
        onClose={() => setOpenCreateMembershipDialog(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          timeout: 400,
        }}
        PaperProps={{
          sx: {
            animation: openCreateMembershipDialog
              ? "slideUp 0.3s ease-out"
              : "none",
            "@keyframes slideUp": {
              from: {
                opacity: 0,
                transform: "translateY(20px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 600 }}>
          Create New Membership
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Membership Name"
            fullWidth
            value={newMembershipName}
            onChange={(e) => setNewMembershipName(e.target.value)}
            placeholder="e.g., Gold Membership, Premium Plan"
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleCreateNewMembership();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setOpenCreateMembershipDialog(false);
              setNewMembershipName("");
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateNewMembership}
            variant="contained"
            disabled={!newMembershipName.trim()}
          >
            Create Membership
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleDeleteFeatureCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            animation: deleteConfirmDialog.open
              ? "slideUp 0.3s ease-out"
              : "none",
            "@keyframes slideUp": {
              from: {
                opacity: 0,
                transform: "translateY(20px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 600 }}>
          Confirm Remove
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "13px", color: "#6C757D" }}>
            Are you sure you want to remove the feature "
            {deleteConfirmDialog.index !== null &&
              features[deleteConfirmDialog.index]?.name}
            "? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteFeatureCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteFeatureConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default MembershipFormSystem;
